/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import { AuthContext } from '../../../Contexts/AuthContext';
import FormDialog from '../../../Components/Modal/modal';
import UseAxios from '../../../Api/http/http';
import {
  createFpBillingType,
  createTmBillingType,
  getBillingTypes,
  updateFpBillingType,
  updateTmBillingType,
} from '../../../Api/request/billingType';
import { getProjectTypeMaster } from '../../../Api/request/projectDetails';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { billingTypeTableConfig } from './billingTypeTableConfig';
import './billingType.scss';

const BILLING_TYPE_MAX_LENGTH = 100;
const PERMISSION_ALIASES = {
  read: ['read', 'view'],
  create: ['create', 'add'],
  update: ['update', 'edit'],
};
const EMPTY_FORM = { name: '', projectTypeKey: '' };

// FP_billing_type / TM_Billing_Type are separate backend tables; the selected
// Project Type determines which table the billing type is stored in.
const PROJECT_TYPE_SOURCE = {
  FP: 'fp',
  TM: 'tm',
};

const getApiErrorMessage = (response) => (
  response?.data?.response?.data?.message
  || response?.data?.message
  || response?.data?.response?.data?.externalMessage
  || ''
);

// Derives the FP/TM table key from the project type master's name without
// hardcoding the master's list of values.
const resolveProjectTypeKey = (projectTypeName) => {
  const value = String(projectTypeName || '').trim()
    .toUpperCase();
  if (!value) return null;
  if (value.includes('T&M') || value.includes('TIME') || value === 'TM') return 'TM';
  if (value.includes('FPP') || value.includes('FIXED') || value === 'FP') return 'FP';
  return null;
};

const normalizeProjectTypeOptions = (response) => {
  const list = response?.data?.projectTypeList || [];
  const optionsByKey = new Map();
  list.forEach((projectType) => {
    const key = resolveProjectTypeKey(projectType.projectTypeName);
    if (!key || optionsByKey.has(key)) return;
    optionsByKey.set(key, {
      key,
      label: projectType.projectTypeName,
      source: PROJECT_TYPE_SOURCE[key],
    });
  });
  return Array.from(optionsByKey.values());
};

const normalizeBillingTypes = (response, source, projectTypeKey, projectTypeLabel) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : response?.data?.data || [];

  return rows.map((item) => normalizeBillingType(item, source, projectTypeKey, projectTypeLabel));
};

const normalizeBillingType = (item, source, projectTypeKey, projectTypeLabel) => ({
  rowKey: `${source}-${item.id}`,
  id: item.id,
  source,
  projectTypeKey,
  projectTypeLabel,
  name: item.name || '',
  isActive: Boolean(item.is_active),
});

const createRequestForSource = {
  [PROJECT_TYPE_SOURCE.FP]: createFpBillingType,
  [PROJECT_TYPE_SOURCE.TM]: createTmBillingType,
};

const updateRequestForSource = {
  [PROJECT_TYPE_SOURCE.FP]: updateFpBillingType,
  [PROJECT_TYPE_SOURCE.TM]: updateTmBillingType,
};

/* eslint-disable react/prop-types -- internal render helper, not a public component */
const BillingTypeRowAction = ({ billingType, hasAccess, onToggle, onEdit }) => (
  <div className="billing-type-actions" onClick={(event) => event.stopPropagation()}>
    <ControlledSwitches
      checked={billingType.isActive}
      disabled={!hasAccess('update')}
      switchCss="switchCss"
      handleChange={() => onToggle(billingType)}
    />
    <button
      type="button"
      className="billing-type-action-btn"
      title="Edit Billing Type"
      aria-label={`Edit ${billingType.name}`}
      disabled={!hasAccess('update')}
      onClick={() => onEdit(billingType)}
    >
      <img src={EditActionIcon} alt="" />
    </button>
  </div>
);
/* eslint-enable react/prop-types */

const buildBillingTypeRows = (billingTypes, hasAccess, onToggle, onEdit) => billingTypes.map(
  (billingType, index) => ({
    ...billingType,
    slNo: index + 1,
    billingTypeName: billingType.name,
    status: billingType.isActive ? 'Active' : 'Inactive',
    action: (
      <BillingTypeRowAction
        billingType={billingType}
        hasAccess={hasAccess}
        onToggle={onToggle}
        onEdit={onEdit}
      />
    ),
  }),
);

const BillingTypeForm = ({
  mode,
  form,
  error,
  isSubmitDisabled,
  projectTypeOptions,
  onChange,
  onClose,
  onSubmit,
}) => createPortal(
  <div className="billing-type-modal-backdrop">
    <form className="billing-type-modal" onSubmit={onSubmit}>
      <div className="billing-type-modal__header">
        {mode === 'edit' ? 'Edit Billing Type' : 'Add Billing Type'}
      </div>
      <div className="billing-type-modal__body">
        <div className="billing-type-modal__field">
          <label htmlFor="billing-type-project-type" className={error.projectTypeKey ? 'is-error' : ''}>
            Project Type<span className="required-asterisk">*</span>
          </label>
          <select
            id="billing-type-project-type"
            name="projectTypeKey"
            value={form.projectTypeKey}
            onChange={onChange}
            className={error.projectTypeKey ? 'is-error' : ''}
            disabled={mode === 'edit'}
          >
            <option value="">Select Project Type</option>
            {projectTypeOptions.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="billing-type-modal__field">
          <label htmlFor="billing-type-name" className={error.name ? 'is-error' : ''}>
            Billing Type<span className="required-asterisk">*</span>
          </label>
          <input
            id="billing-type-name"
            name="name"
            type="text"
            placeholder="Enter Billing Type"
            value={form.name}
            maxLength={BILLING_TYPE_MAX_LENGTH}
            onChange={onChange}
            className={error.name ? 'is-error' : ''}
            autoComplete="off"
          />
        </div>
      </div>
      <div className="billing-type-modal__footer">
        <button type="button" className="billing-type-modal__btn billing-type-modal__btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="billing-type-modal__btn billing-type-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const BillingType = () => {
  const { userProfile } = React.useContext(AuthContext);
  const permissionKey = JSON.stringify(
    userProfile?.permissions?.[MODULE_NAMES.CONFIGURATION_BILLING_TYPE] || [],
  );
  const isSuperAdmin = userProfile?.roleDetails?.some(
    (role) => role.roleName === 'Super Admin',
  );
  const normalizedPermissions = useMemo(
    () => JSON.parse(permissionKey).map((action) => String(action).toLowerCase()),
    [permissionKey],
  );
  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    return PERMISSION_ALIASES[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);

  const [billingTypes, setBillingTypes] = useState([]);
  const [projectTypeOptions, setProjectTypeOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, mode: 'add', rowKey: null });
  const [toggleItem, setToggleItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasError, setHasError] = useState({ name: false, projectTypeKey: false });

  const loadProjectTypeOptions = useCallback(async () => {
    if (!hasAccess('read')) return [];
    const response = await UseAxios(getProjectTypeMaster());
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return [];
    }
    const options = normalizeProjectTypeOptions(response);
    setProjectTypeOptions(options);
    return options;
  }, [hasAccess]);

  const loadBillingTypes = useCallback(async (options) => {
    if (!hasAccess('read')) return;
    const optionsByKey = new Map(options.map((option) => [option.key, option]));
    const response = await UseAxios(getBillingTypes(), setIsLoading);

    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    const fpOption = optionsByKey.get('FP');
    const tmOption = optionsByKey.get('TM');
    const fpRows = normalizeBillingTypes(
      { data: response.data?.fpBillingTypes || [] },
      PROJECT_TYPE_SOURCE.FP,
      'FP',
      fpOption?.label || 'FPP',
    );
    const tmRows = normalizeBillingTypes(
      { data: response.data?.tmBillingTypes || [] },
      PROJECT_TYPE_SOURCE.TM,
      'TM',
      tmOption?.label || 'T&M',
    );
    setBillingTypes([...fpRows, ...tmRows]);
  }, [hasAccess]);

  useEffect(() => {
    (async () => {
      const options = await loadProjectTypeOptions();
      await loadBillingTypes(options);
    })();
  }, [loadBillingTypes, loadProjectTypeOptions]);

  const closeForm = useCallback(() => {
    setDialog({ isOpen: false, mode: 'add', rowKey: null });
    setForm(EMPTY_FORM);
    setHasError({ name: false, projectTypeKey: false });
  }, []);

  const openAdd = useCallback(() => {
    if (!hasAccess('create')) return;
    setDialog({ isOpen: true, mode: 'add', rowKey: null });
    setForm(EMPTY_FORM);
    setHasError({ name: false, projectTypeKey: false });
  }, [hasAccess]);

  const openEdit = useCallback((billingType) => {
    if (!hasAccess('update')) return;
    setDialog({ isOpen: true, mode: 'edit', rowKey: billingType.rowKey });
    setForm({ name: billingType.name, projectTypeKey: billingType.projectTypeKey });
    setHasError({ name: false, projectTypeKey: false });
  }, [hasAccess]);

  const selectedBillingType = billingTypes.find((item) => item.rowKey === dialog.rowKey);
  const isSubmitDisabled = !form.name.trim()
    || !form.projectTypeKey
    || (dialog.mode === 'edit' && form.name.trim() === selectedBillingType?.name.trim());

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!hasAccess(dialog.mode === 'edit' ? 'update' : 'create')) return;
    const name = form.name.trim();
    const errors = { name: !name, projectTypeKey: !form.projectTypeKey };
    if (errors.name || errors.projectTypeKey) {
      setHasError(errors);
      toast.error('Please select a project type and enter a billing type.');
      return;
    }

    const source = dialog.mode === 'edit' ? selectedBillingType?.source : PROJECT_TYPE_SOURCE[form.projectTypeKey];
    const response = dialog.mode === 'edit'
      ? await UseAxios(
        updateRequestForSource[source](selectedBillingType.id, name, selectedBillingType?.isActive ?? true),
        setIsLoading,
      )
      : await UseAxios(createRequestForSource[source](name), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    toast.success(dialog.mode === 'edit'
      ? 'Billing type updated successfully.'
      : 'Billing type added successfully.');

    const projectTypeOption = projectTypeOptions.find((option) => option.source === source);
    const savedBillingType = normalizeBillingType(
      response.data,
      source,
      projectTypeOption?.key || form.projectTypeKey,
      projectTypeOption?.label,
    );
    setBillingTypes((current) => (
      dialog.mode === 'edit'
        ? current.map((item) => (item.rowKey === savedBillingType.rowKey ? savedBillingType : item))
        : [...current, savedBillingType]
    ));
    closeForm();
  }, [closeForm, dialog.mode, form.name, form.projectTypeKey, hasAccess, projectTypeOptions, selectedBillingType]);

  const handleToggle = useCallback((billingType) => {
    if (!hasAccess('update')) return;
    setToggleItem(billingType);
  }, [hasAccess]);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleItem(null);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleItem || !hasAccess('update')) return;

    const nextStatus = !toggleItem.isActive;
    const response = await UseAxios(
      updateRequestForSource[toggleItem.source](toggleItem.id, toggleItem.name, nextStatus),
      setIsLoading,
    );
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setBillingTypes((current) => current.map((item) => (
      item.rowKey === toggleItem.rowKey
        ? { ...item, isActive: nextStatus }
        : item
    )));
    toast.success(`${toggleItem.name} is now ${nextStatus ? 'active' : 'inactive'}.`);
    handleCloseToggleDialog();
  }, [handleCloseToggleDialog, hasAccess, toggleItem]);

  const tableData = useMemo(
    () => buildBillingTypeRows(billingTypes, hasAccess, handleToggle, openEdit),
    [billingTypes, handleToggle, hasAccess, openEdit],
  );

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {},
      handlers: { handleAddIconClick: openAdd },
      toolBarSettings: {
        showAddNew: hasAccess('create'),
        showFilterOption: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Billing Type',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: billingTypeTableConfig,
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: billingTypeTableConfig.length },
      paginationConfig: { page: 1, limit: tableData.length, total: tableData.length, totalPages: 1 },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [hasAccess, openAdd, tableData.length]);

  if (!hasAccess('read')) {
    return <div>You do not have permission to view billing types.</div>;
  }

  return (
    <div className="billing-type-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialog.isOpen && (
        <BillingTypeForm
          mode={dialog.mode}
          form={form}
          error={hasError}
          isSubmitDisabled={isSubmitDisabled}
          projectTypeOptions={projectTypeOptions}
          onChange={(event) => {
            setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
            setHasError((current) => ({ ...current, [event.target.name]: false }));
          }}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}
      <FormDialog
        open={Boolean(toggleItem)}
        title={`${toggleItem?.isActive ? 'Disable' : 'Enable'} Billing Type`}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {`Are you sure you want to ${toggleItem?.isActive ? 'disable' : 'enable'} this billing type?`}
            </div>
          </div>
        )}
        button1="Cancel"
        cancelButtonId="cancel-billing-type-toggle"
        button2={toggleItem?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="confirm-billing-type-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggle}
      />
    </div>
  );
};

export default BillingType;
